import { getPatientFileSidebarTabItems } from '@components/PatientFile/patientFileSidebarTabItems';
import { runAfterModalUnwind } from '@/composables/runAfterModalUnwind';
import { PatientFileMark } from '@src/const/PatientFileMarks';
import { useModalStore } from '@stores/modalStore';
import { useSidebarStore } from '@stores/sidebarStore';
import { useToastStore } from '@stores/toastStore';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { appointmentsMutations, notificationsKeys } from '@therame/api-client';
import { ColorVariants } from '@therame/design-tokens/src/color-variants';
import { CANCELLATION_REASON_LABELS } from '@components/AppointmentOverview/cancellationReasonLabels';
import {
  CallPatientStatus,
  getNotificationContent,
  getNotificationTimeLabel,
  getPath,
  NotificationFullSizeDto,
  NotificationType,
  type NotificationActionKey,
  type NotificationButton,
  type NotificationButtonAction,
} from '@therame/shared';
import type { MingcuteIconName, PopUpBannerButton, PopUpBannerSegment } from '@therame/ui';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { patchNotificationInCache } from './patchNotificationInCache';

export interface UseNotificationBannerOptions {
  /**
   * Called once an accept/decline action has been persisted for a notification.
   * The transient toast container wires this to `dismiss` so the acted invite
   * toast disappears; the inbox leaves it unset and relies on the cache patch
   * (below) to drop the buttons in place.
   */
  onActed?: (notificationId: string) => void;
}

export interface NotificationBannerProps {
  headline: string;
  icon: MingcuteIconName;
  variant: ColorVariants;
  contentText: string;
  segments?: PopUpBannerSegment[];
  buttons?: PopUpBannerButton[];
  timestampLabel?: string;
}

const isParticipantAction = (
  actionKey: NotificationActionKey | NotificationButtonAction,
): actionKey is NotificationButtonAction =>
  actionKey === 'APPOINTMENT_ACCEPT' || actionKey === 'APPOINTMENT_DECLINE';

/**
 * Bündelt die gesamte Darstellungs-Logik der Backend-Benachrichtigungen: mappt
 * eine Notification über den zentralen Composer (`@therame/shared`) auf
 * GenericPopUpBanner-Props und verdrahtet die Klick-Aktionen (Navigation,
 * Termin-Zu-/Absage, externe Links).
 *
 * So braucht es keine eigene Wrapper-Komponente mehr – Container und Posteingang
 * rendern den GenericPopUpBanner direkt mit `bannerPropsFor(notification)`.
 */
export const useNotificationBanner = ({ onActed }: UseNotificationBannerOptions = {}) => {
  const router = useRouter();
  const sidebarStore = useSidebarStore();
  const modalStore = useModalStore();
  const toastStore = useToastStore();
  const queryClient = useQueryClient();

  // Pending-Status pro Notification, damit nur das geklickte Invite-Banner lädt
  // (eine geteilte mutation.isPending würde alle offenen Invites gleichzeitig sperren).
  const pendingNotificationIds = ref<Set<string>>(new Set<string>());

  const attendanceMutation = useMutation({
    mutationFn: ({
      notification,
      status,
    }: {
      notification: NotificationFullSizeDto;
      status: CallPatientStatus;
    }) =>
      appointmentsMutations.updateOwnParticipantStatus(`${notification.appointmentId}`, { status }),
    // Erst nach bestätigtem Server-Write auflösen: der zugestellte ownParticipantStatus
    // ist maßgeblich dafür, dass der Composer die Buttons ausblendet. Wir patchen den
    // Wert in jeden Notification-Cache (der Posteingang verliert seine Buttons an Ort und
    // Stelle) und melden die Aktion dem Aufrufer (der Toast blendet sich aus).
    onSuccess: (_data, { notification, status }) => {
      const id = `${notification.id}`;
      queryClient.setQueriesData({ queryKey: notificationsKeys.all() }, (data) =>
        patchNotificationInCache(data, id, { ownParticipantStatus: status }),
      );
      onActed?.(id);
    },
    onError: (_error, { status }) => {
      const verb = status === CallPatientStatus.ACCEPTED ? 'bestätigt' : 'abgelehnt';
      toastStore.addToast({
        type: 'error',
        text: `Der Termin konnte nicht ${verb} werden. Bitte versuche es erneut.`,
      });
    },
  });

  /**
   * Every one of these actions is "put the Posteingang away and go somewhere",
   * and the Posteingang is a modal: closing it pops the history entry it pushed
   * when it opened (`useHistoryIntercept`). A `router.push` in the same breath
   * races that `history.back()` and gets reverted — the press reads as a dead
   * button. So the close happens first and the navigation waits for the unwind,
   * via `runAfterModalUnwind`. When nothing is closing (the transient toasts
   * reuse these same handlers) it navigates straight away.
   */
  const openExerciseExecution = () => {
    modalStore.closeModal({ type: 'notifications', action: 'cancel' });
    runAfterModalUnwind(() =>
      router.push(getPath<'exerciseExecution'>('/exercise-execution/overview', {})),
    );
  };

  const goToPatientFile = (
    notification: NotificationFullSizeDto,
    initialTabView: PatientFileMark,
  ) => {
    modalStore.closeModal({ type: 'notifications', action: 'cancel' });
    const patientId = notification.actorId;
    if (!patientId) return;
    // The sidebar open is deferred with the push: it pushes a history entry of
    // its own, which the inbox's pending `history.back()` would swallow.
    runAfterModalUnwind(() => {
      sidebarStore.openSidebar({
        type: 'patientFile',
        props: { patientId },
        sidebarTabItems: getPatientFileSidebarTabItems(),
        initialTabView,
      });
      router.push(getPath<'patientAccountList'>('/patient/account-list', {}));
    });
  };

  const runAction = (action: NotificationActionKey, notification: NotificationFullSizeDto) => {
    switch (action) {
      case 'OPEN_EXERCISE_EXECUTION':
        openExerciseExecution();
        break;
      case 'GO_TO_PATIENT_PROGRAM':
        goToPatientFile(notification, PatientFileMark.PROGRAM);
        break;
      case 'GO_TO_PATIENT_STATISTICS':
        // Statistics live in the merged Programme tab (below the program list).
        goToPatientFile(notification, PatientFileMark.PROGRAM);
        break;
    }
  };

  const openHref = (href: string) => {
    if (/^https?:\/\//.test(href)) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    modalStore.closeModal({ type: 'notifications', action: 'cancel' });
    runAfterModalUnwind(() => router.push(href));
  };

  const setPending = (id: string, pending: boolean) => {
    const next = new Set(pendingNotificationIds.value);
    if (pending) next.add(id);
    else next.delete(id);
    pendingNotificationIds.value = next;
  };

  const runButtonAction = (
    action: NotificationButtonAction,
    notification: NotificationFullSizeDto,
  ) => {
    const id = `${notification.id}`;
    setPending(id, true);
    attendanceMutation.mutate(
      {
        notification,
        status:
          action === 'APPOINTMENT_ACCEPT' ? CallPatientStatus.ACCEPTED : CallPatientStatus.DECLINED,
      },
      { onSettled: () => setPending(id, false) },
    );
  };

  const onButtonClick = (button: NotificationButton, notification: NotificationFullSizeDto) => {
    if (button.href) {
      openHref(button.href);
      return;
    }
    if (!button.actionKey) return;
    if (isParticipantAction(button.actionKey)) {
      runButtonAction(button.actionKey, notification);
      return;
    }
    runAction(button.actionKey, notification);
  };

  /** Liefert die GenericPopUpBanner-Props oder `null`, wenn der Typ nicht angezeigt wird. */
  /**
   * Detail the app shows but the shared content leaves out, because mail and
   * push send that content as is. The reason for an Absage can be health data
   * ("Krankheit"), so it never leaves the app (TM-9535).
   */
  const inAppOnlyDetail = (notification: NotificationFullSizeDto): string =>
    notification.type === NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT &&
    notification.cancellationReason
      ? ` Grund: ${CANCELLATION_REASON_LABELS[notification.cancellationReason]}.`
      : '';

  const bannerPropsFor = (
    notification: NotificationFullSizeDto,
  ): NotificationBannerProps | null => {
    const content = getNotificationContent(notification);
    if (!content) return null;

    return {
      headline: content.title,
      icon: content.icon,
      variant: content.variant,
      contentText: content.text + inAppOnlyDetail(notification),
      segments: content.segments,
      buttons: content.buttons?.map((button) => ({
        text: button.text,
        colorVariant: button.colorVariant,
        icon: button.icon,
        // Only accept/decline invites carry per-notification pending state.
        loading: pendingNotificationIds.value.has(`${notification.id}`),
        // One hook for whatever the notification's own action is ("Jetzt
        // starten", "Zum Programm", …), so a spec can press it without
        // matching German copy. Archivieren carries its own.
        dataCy: 'notificationActionButton',
        onClick: () => onButtonClick(button, notification),
      })),
      timestampLabel: getNotificationTimeLabel(notification),
    };
  };

  return { bannerPropsFor };
};
